import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '../../../../shared/components/Alert/Alert';
import Loader from '../../../../shared/components/Loader/Loader';
import Input from '../../../../shared/components/Input/Input';
import { getPartner } from '../../partners/partner.api';
import { getAffairContacts, updateAffairContacts } from '../affair.api';

const contactName = (contact) => contact.contactName || contact.contact_name || [contact.firstName || contact.first_name, contact.lastName || contact.last_name].filter(Boolean).join(' ') || `Contact ${contact.id || contact.contactId}`;
const normalizeLink = (link) => ({ ...link, contactId: link.contactId || link.contact_id, role: link.role || '',
  isPrimary: Boolean(link.isPrimary ?? link.is_primary), isTechnical: Boolean(link.isTechnical ?? link.is_technical),
  isAdministrative: Boolean(link.isAdministrative ?? link.is_administrative), isBilling: Boolean(link.isBilling ?? link.is_billing), notes: link.notes || '' });
const contactPayload = ({ contactId, role, isPrimary, isTechnical, isAdministrative, isBilling, notes }) => ({
  contactId: Number(contactId), role, isPrimary, isTechnical, isAdministrative, isBilling, notes,
});

export default function AffairContactsPanel({ affair }) {
  const [links, setLinks] = useState([]);
  const [available, setAvailable] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [linkedRows, partner] = await Promise.all([getAffairContacts(affair.id), getPartner(affair.companyId)]);
      setLinks((Array.isArray(linkedRows) ? linkedRows : []).map(normalizeLink));
      setAvailable(partner?.Contacts || partner?.contacts || []);
      setError('');
    } catch (requestError) { setError(requestError.message); } finally { setLoading(false); }
  }, [affair.companyId, affair.id]);

  useEffect(() => { load(); }, [load]);
  const availableToAdd = useMemo(() => available.filter((contact) => {
    const id = contact.id || contact.contactId || contact.contact_id;
    return id && !links.some((link) => String(link.contactId) === String(id));
  }), [available, links]);
  const add = async () => {
    const contact = available.find((item) => String(item.id || item.contactId || item.contact_id) === String(selectedId));
    if (!contact) return;
    const nextLinks = [...links, normalizeLink({ ...contact, contactId: contact.id || contact.contactId || contact.contact_id })];
    try {
      setSaving(true); setError('');
      await updateAffairContacts(affair.id, { contacts: nextLinks.map(contactPayload) });
      setSelectedId('');
      await load();
    } catch (requestError) { setError(requestError.message || 'Impossible d’ajouter ce contact à l’affaire.'); }
    finally { setSaving(false); }
  };
  const update = (contactId, field, value) => setLinks((current) => current.map((link) => ({ ...link,
    ...(field === 'isPrimary' && value ? { isPrimary: String(link.contactId) === String(contactId) } : {}),
    ...(String(link.contactId) === String(contactId) ? { [field]: value } : {}),
  })));
  const save = async () => {
    try {
      setSaving(true);
      await updateAffairContacts(affair.id, { contacts: links.map(contactPayload) });
      await load();
    } catch (requestError) { setError(requestError.message); } finally { setSaving(false); }
  };

  if (loading) return <Loader label="Chargement des contacts..." />;
  return <section className="affair-panel"><div className="affair-panel__header"><div><h2>Contacts de l’affaire</h2><p>Le retrait d’un rattachement ne supprime pas le contact CRM.</p></div><button className="basic-button active" type="button" onClick={save} disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</button></div>
    {error && <Alert severity="error">{error}</Alert>}
    <div className="affair-contact-add">
      <div className="affair-contact-add__field">
        <label className="custom-input__label" htmlFor="affair-contact-select">Contact à rattacher</label>
        <div className="affair-contact-add__select-shell">
          <select id="affair-contact-select" aria-label="Contact à ajouter" className="custom-input__field" value={selectedId} onChange={(event) => setSelectedId(event.target.value)} disabled={saving}>
            <option value="">Sélectionner un contact</option>
            {availableToAdd.map((contact) => { const contactId = contact.id || contact.contactId || contact.contact_id; return <option key={contactId} value={contactId}>{contactName(contact)}</option>; })}
          </select>
        </div>
        {availableToAdd.length === 0 && <span className="affair-contact-add__help">Tous les contacts du partenaire sont déjà rattachés, ou aucun contact n’a encore été créé.</span>}
      </div>
      <button className="basic-button" type="button" disabled={!selectedId || saving} onClick={add}>{saving ? 'Ajout...' : 'Ajouter'}</button>
    </div>
    <div className="affair-contact-list">{links.length === 0 ? <p>Aucun contact rattaché.</p> : links.map((link) => <article className="affair-contact-row" key={link.contactId}>
      <div><strong>{contactName(link)}</strong><span>{link.agencyName || link.agency_name || 'Agence non renseignée'}</span><span>{[link.phone, link.mobilePhone, link.email].filter(Boolean).join(' · ') || 'Coordonnées non renseignées'}</span></div>
      <Input label="Rôle" value={link.role} onChange={(event) => update(link.contactId, 'role', event.target.value)} />
      <div className="affair-contact-flags">{[['isPrimary','Principal'],['isTechnical','Technique'],['isAdministrative','Administratif'],['isBilling','Facturation']].map(([field,label]) => <label key={field}><input type="checkbox" checked={link[field]} onChange={(event) => update(link.contactId, field, event.target.checked)} /> {label}</label>)}</div>
      <button className="basic-button" type="button" onClick={() => setLinks((current) => current.filter((item) => item.contactId !== link.contactId))}>Retirer</button>
    </article>)}</div>
  </section>;
}
